import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react';
import en from './en';
import ru from './ru';
import uz from './uz';
import namespaces from './ns';
import { DEFAULT_LANG, getActiveLang, rememberLang } from './lang';
import { interpolate } from './interpolate';

// Merge the base dictionary with the per-namespace modules (ns/*.js).
// Each module is { en, ru, uz }. A missing translation falls back to Uzbek (the default
// language), then English; a parity test (__tests__/parity.test.js) keeps all three complete.
function buildDict(lang, base) {
  const dict = { ...base };
  Object.entries(namespaces).forEach(([name, mod]) => {
    dict[name] = mod[lang] || mod[DEFAULT_LANG] || mod.en || {};
  });
  return dict;
}

const DICTS = { uz: buildDict('uz', uz), en: buildDict('en', en), ru: buildDict('ru', ru) };

// UZ · EN · RU, in that order, everywhere a language is offered.
export const LANGUAGES = [
  { code: 'uz', label: "O'zbekcha", short: 'UZ' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ru', label: 'Русский', short: 'RU' },
];

const I18nContext = createContext(null);

function resolve(dict, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

// Keys already reported, so the development console warns once per missing key.
const warned = new Set();
function warnMissing(key, lang) {
  if (process.env.NODE_ENV !== 'development') return;
  const id = `${lang}:${key}`;
  if (warned.has(id)) return;
  warned.add(id);
  console.warn(`[i18n] "${key}" has no ${lang} translation`);
}

export function I18nProvider({ children }) {
  // Uzbek unless the user switched earlier. A stored choice always wins; the browser's
  // language is never consulted.
  const [lang, setLangState] = useState(getActiveLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Only the user's own switch is stored, so a default is never mistaken for a choice.
  const setLang = useCallback((next) => {
    if (!DICTS[next]) return;
    rememberLang(next);
    setLangState(next);
  }, []);

  // t('a.b.c', { name: 'X' }) → translated string with {name} interpolation.
  // Falls back: current lang → Uzbek → English → the key itself.
  const t = useCallback((key, vars) => {
    let s = resolve(DICTS[lang], key);
    if (s == null && lang !== DEFAULT_LANG) s = resolve(DICTS[DEFAULT_LANG], key);
    if (s == null) {
      s = resolve(DICTS.en, key);
      if (s != null) warnMissing(key, lang);
    }
    if (s == null) return key;
    return interpolate(s, vars);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) return { lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k, languages: LANGUAGES };
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export default I18nContext;
